import { useEffect, useState } from 'react';

/**
 * Custom hook for negating a value after a non-zero delay.
 * Useful for resetting `boolean`-based states.
 *
 * @param {boolean} flag value of interest to negate after set time period has elapsed.
 * @param {Number} [delay=0] millisecond interval delaying negation of value of interest.
 * @returns {boolean} value of interest, negated after defined time period delay.
 */
const useDelayNegate = (flag, delay = 0) => {
	if (typeof flag === 'boolean') {
		const [debouncedFlag, setDebouncedFlag] = useState(
			delay > 0 ? flag : !flag
		);

		/**
		 * Lifecycle hook on parameter change. Negates parameter `flag` after parameter `delay` time period has elapsed.
		 * @returns {Function} clean-up clearing timeout.
		 */
		useEffect(() => {
			let timeoutID;
			if (delay > 0) {
				timeoutID = setTimeout(() => {
					setDebouncedFlag(!flag);
				}, delay);
			}

			return () => {
				clearTimeout(timeoutID);
			};
		}, [flag, delay]);

		return debouncedFlag;
	} else {
		throw new TypeError(
			'Incorrect parameter type, expected `boolean`.'
		);
	}
};

export { useDelayNegate };
